import { LucideIconsModule } from '../../../shared/lucide-icons/lucide-icons.module';
import { NgClass } from '@angular/common';
import { Component, HostBinding, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { Subscription, combineLatest, filter, startWith } from 'rxjs';
import { CommonService } from '../../../services/common.service';
import { UserAccessService } from '../../../services/user-access.service';
import { filterMenuForPos } from '../../../utils/pos.util';
import { shouldShowMobileBottomNav } from '../../../utils/mobile-bottom-nav.util';
import {
  ALL_SIDE_MENU_SECTIONS,
  MobileMoreMenuItem,
  SideMenuItem,
  SideMenuSection,
  buildMobileMoreMenuSections,
  isMenuItemActive,
  toMobileMoreMenuItem,
} from '../../../utils/side-menu.config';
import { allowedPathsForRole, filterMenuByRolePaths } from '../../../utils/rbac-routes.util';

@Component({
  selector: 'app-mobile-bottom',
  imports: [
    LucideIconsModule, NgClass, RouterLink, NzIconModule, NzDrawerModule],
  templateUrl: './mobile-bottom.component.html',
  styleUrl: './mobile-bottom.component.css',
})
export class MobileBottomComponent implements OnInit, OnDestroy {
  @Input() activeRoute: string = '';

  /** Defense in depth — hide even if the shell forgets to unmount us. */
  @HostBinding('attr.hidden')
  get hostHidden(): '' | null {
    return shouldShowMobileBottomNav(this.activeRoute) ? null : '';
  }

  private commonService = inject(CommonService);
  private userAccess = inject(UserAccessService);
  private menuSub?: Subscription;
  private routeSub?: Subscription;
  private allowedMenuPaths: readonly string[] | null = null;

  // Main navigation items (visible in bottom bar)
  private readonly allMainNavItems = [
    { icon: 'layout-dashboard', name: 'Home', path: '/dashboard', badge: null },
    { icon: 'users', name: 'Members', path: '/members', badge: null },
    { icon: 'circle-dot', name: 'Loyalty', path: '/loyalty-programs', badge: null },
    { icon: 'gift', name: 'Rewards', path: '/rewards', badge: null },
  ];

  private readonly billerMainNavItems = [
    { icon: 'users', name: 'Members', path: '/members', badge: null },
    { icon: 'receipt', name: 'Transactions', path: '/transactions', badge: null },
  ];

  mainNavItems: any[] = [...this.allMainNavItems];

  private readonly allSideMenuSections = ALL_SIDE_MENU_SECTIONS;
  moreMenuSections: SideMenuSection[] = [];
  private userRole = '';

  showMoreMenu = false;

  constructor(private router: Router) { }

  ngOnInit(): void {
    this.refreshMoreMenuSections();
    this.menuSub = combineLatest([
      this.commonService.selectedOutlet.pipe(startWith(null)),
      this.userAccess.fetchAccess(),
    ]).subscribe(([outlet, access]) => {
      this.userRole = access.role;
      this.allowedMenuPaths = allowedPathsForRole(access.role);
      const baseMain =
        access.role === 'biller' ? this.billerMainNavItems : this.allMainNavItems;
      this.mainNavItems = filterMenuByRolePaths(
        filterMenuForPos(baseMain, (outlet as any)?.posName),
        this.allowedMenuPaths,
        access.role,
      );
      this.refreshMoreMenuSections((outlet as any)?.posName);
    });

    // set on load
    this.activeRoute = this.router.url.split('?')[0].split(';')[0];

    // update on navigation
    this.routeSub = this.router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.activeRoute = event.urlAfterRedirects.split('?')[0].split(';')[0];
        this.closeMoreMenu();
      });
  }

  ngOnDestroy(): void {
    this.menuSub?.unsubscribe();
    this.routeSub?.unsubscribe();
  }

  toggleMoreMenu() {
    this.showMoreMenu = !this.showMoreMenu;
  }

  closeMoreMenu() {
    this.showMoreMenu = false;
  }

  getMoreMenuItem(item: SideMenuItem): MobileMoreMenuItem {
    return toMobileMoreMenuItem(item);
  }

  get moreNavItems(): MobileMoreMenuItem[] {
    return this.moreMenuSections.flatMap((section) =>
      section.items.map((item) => toMobileMoreMenuItem(item)),
    );
  }

  isItemActive(itemPath: string): boolean {
    return isMenuItemActive(itemPath, this.activeRoute, this.allMenuPaths);
  }

  /** More is active when its drawer is open, or the current page lives under More. */
  get isMoreActive(): boolean {
    if (this.showMoreMenu) return true;
    if (!this.activeRoute) return false;
    if (this.mainNavItems.some((item) => this.isItemActive(item.path))) return false;
    return this.moreMenuSections.some((section) =>
      section.items.some((item) => this.isItemActive(item.path)),
    );
  }

  private get allMenuPaths(): string[] {
    return ALL_SIDE_MENU_SECTIONS.flatMap((section) =>
      section.items.map((item) => item.path),
    );
  }

  private refreshMoreMenuSections(posName?: string | null): void {
    const mainPaths = this.mainNavItems.map((item) => item.path);
    this.moreMenuSections = buildMobileMoreMenuSections(
      this.allSideMenuSections,
      mainPaths,
      posName,
      this.allowedMenuPaths,
      this.userRole,
    );
  }
}
